import type { TEscalateConversationResponsePayload } from '@cascade/api-client/ticketing';
import type { TGetTicketingSystemOptionsResponsePayload } from '@cascade/api-client/ticketing-integrations';
import { getDisplayName } from '@cascade/core/auth';
import { useConversationThread } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useProjects } from '@cascade/react/projects';
import { useTickets } from '@cascade/react/tickets';
import { useUser } from '@cascade/react/user';
import {
  Button,
  ChatMessages,
  Dialog,
  Link,
  Loading,
  Select,
  Text,
  TextArea,
} from '@cascade/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildCustomFields,
  buildInitialValues,
  hasMissingRequiredField,
  hasOverLengthField,
  splitFieldErrors,
} from './CreateTicketDialog.helpers.ts';
import classes from './CreateTicketDialog.module.scss';
import type {
  TCreateTicketDialogProps,
  TCreateTicketStep,
  TTicketFieldErrors,
  TTicketFieldValues,
} from './CreateTicketDialog.types.ts';
import { TicketField } from './ticket-field/index.ts';
import type { TTicketFieldValue } from './ticket-field/index.ts';
import { getTicketPath } from '#config/index.ts';
import { buildConversationMessage } from '#helpers/index.ts';

const CASCADE_TYPE = 'cascade';
const DESCRIPTION_ROWS = 3;

export const CreateTicketDialog = ({
  conversationId,
  messageId,
  onClose,
}: TCreateTicketDialogProps) => {
  const { translate, translateError } = useI18n();
  const { user } = useUser();
  const { settings } = useProjects();
  const { messages } = useConversationThread();
  const { isCreatingTicket, getTicketingOptions, createTicket } = useTickets();
  const navigate = useNavigate();

  const [step, setStep] = useState<TCreateTicketStep>('information');
  const [options, setOptions] =
    useState<TGetTicketingSystemOptionsResponsePayload>();

  const [optionsError, setOptionsError] = useState<unknown>();
  const [selectedSystemId, setSelectedSystemId] = useState<string>();
  const [description, setDescription] = useState('');
  const [fieldValues, setFieldValues] = useState<TTicketFieldValues>({});
  const [fieldErrors, setFieldErrors] = useState<TTicketFieldErrors>({});
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);
  const [createdTicket, setCreatedTicket] =
    useState<TEscalateConversationResponsePayload>();

  const userName = getDisplayName(user);
  const agentName = translate('agent.name');
  const systems = useMemo(() => options?.ticketing_systems ?? [], [options]);

  const hasSystems = systems.length > 0;
  const selectedSystem = systems.find(
    (system) => system.id === selectedSystemId,
  );

  const isLoadingOptions = options === undefined && optionsError === undefined;
  const isDestinationMissing = hasSystems && selectedSystem === undefined;
  const isProjectSupport = options !== undefined && !hasSystems;
  const projectSupportChannel = isProjectSupport
    ? settings?.hr_support_channel
    : undefined;

  const projectSupportDialogConfig = isProjectSupport
    ? settings?.hr_support_dialog_config
    : undefined;

  const destinationType =
    selectedSystem?.ticketing_type ?? projectSupportChannel;

  const isCascadeDestination = destinationType === CASCADE_TYPE;
  const dialogConfig =
    selectedSystem?.dialog_config ?? projectSupportDialogConfig;

  const fields = selectedSystem?.escalation_fields ?? [];
  const trimmedDescription = description.trim();
  const isDone = step === 'confirmation';
  const isForm = step === 'form';

  const canContinue =
    !isLoadingOptions && optionsError === undefined && !isDestinationMissing;

  const canSubmit =
    canContinue &&
    !isCreatingTicket &&
    trimmedDescription.length > 0 &&
    !hasMissingRequiredField(fields, fieldValues) &&
    !hasOverLengthField(fields, fieldValues);

  const title = isDone
    ? translate('ticket.create.done')
    : (dialogConfig?.title ?? translate('ticket.create.title'));

  const historyMessages = useMemo(
    () =>
      messages.map((message) =>
        buildConversationMessage(message, { userName, agentName }),
      ),
    [messages, userName, agentName],
  );

  const systemOptions = useMemo(
    () => systems.map((system) => ({ label: system.name, value: system.id })),
    [systems],
  );

  const selectSystem = (systemId?: string) => {
    const system = systems.find((candidate) => candidate.id === systemId);

    setSelectedSystemId(systemId);
    setFieldValues(buildInitialValues(system?.escalation_fields ?? []));
    setDescription('');
    setFieldErrors({});
    setSubmissionErrors([]);
  };

  const changeField = (key: string, value: TTicketFieldValue) => {
    setFieldValues((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => {
      const next = { ...previous };

      delete next[key];

      return next;
    });
  };

  const goToForm = () => setStep('form');
  const goToInformation = () => setStep('information');

  const finish = (response: TEscalateConversationResponsePayload) => {
    if (isCascadeDestination) {
      const ticketPath = getTicketPath(response.display_id);

      navigate(ticketPath);
      onClose();

      return;
    }

    setCreatedTicket(response);
    setStep('confirmation');
  };

  const refuse = (response: TEscalateConversationResponsePayload) => {
    const { known, unknown } = splitFieldErrors(
      fields,
      response.field_errors ?? [],
    );

    setFieldErrors(known);
    setSubmissionErrors([
      response.message || translate('ticket.create.failed'),
      ...unknown,
    ]);
  };

  const submit = () => {
    setSubmissionErrors([]);

    createTicket({
      conversation_id: conversationId,
      message: trimmedDescription,
      custom_fields: buildCustomFields(fields, fieldValues),
      ticketing_system_id: selectedSystem?.id,
    })
      .then((response) =>
        response.success ? finish(response) : refuse(response),
      )
      .catch((error: unknown) => setSubmissionErrors([translateError(error)]));
  };

  useEffect(() => {
    getTicketingOptions({
      conversation_id: conversationId,
      message_id: messageId,
    })
      .then((response) => {
        const hinted = response.ticketing_systems.find(
          (system) => system.id === response.ticketing_system_id_hint,
        );

        setOptions(response);
        setSelectedSystemId(hinted?.id);
        setFieldValues(buildInitialValues(hinted?.escalation_fields ?? []));
      })
      .catch(setOptionsError);
  }, [conversationId, messageId, getTicketingOptions]);

  return (
    <Dialog
      open
      size='md'
      title={title}
      closeLabel={translate('ticket.create.close')}
      footer={
        <div className={classes.actions}>
          {isDone ? (
            <Button
              variant='primary'
              label={translate('ticket.create.close')}
              onClick={onClose}
            />
          ) : (
            <Button
              variant='secondary'
              label={translate('ticket.create.cancel')}
              disabled={isCreatingTicket}
              onClick={onClose}
            />
          )}
          {isForm ? (
            <>
              <Button
                variant='secondary'
                label={translate('ticket.create.back')}
                disabled={isCreatingTicket}
                onClick={goToInformation}
              />
              <Button
                variant='primary'
                label={translate('ticket.create.submit')}
                disabled={!canSubmit}
                onClick={submit}
              />
            </>
          ) : null}
          {step === 'information' ? (
            <Button
              variant='primary'
              label={translate('ticket.create.continue')}
              disabled={!canContinue}
              onClick={goToForm}
            />
          ) : null}
        </div>
      }
      onClose={onClose}
    >
      <div className={classes.body}>
        {step === 'information' ? (
          <>
            {isLoadingOptions ? <Loading contain /> : null}
            {optionsError !== undefined ? (
              <Text
                type='body-2'
                color='text-danger'
                role='alert'
                content={translate('ticket.create.destinationsFailed')}
              />
            ) : null}
            {hasSystems ? (
              <Select
                label={translate('ticket.create.destination')}
                placeholder={translate('ticket.create.destinationPlaceholder')}
                options={systemOptions}
                value={selectedSystemId}
                minSelected={1}
                onChange={selectSystem}
              />
            ) : null}
            <Text
              type='body-2'
              content={
                dialogConfig?.description_step_info ??
                translate('ticket.create.info')
              }
            />
            <Text
              type='body-2'
              content={
                dialogConfig?.description_step_privacy_disclosure ??
                translate('ticket.create.privacy')
              }
            />
          </>
        ) : null}
        {isForm ? (
          <>
            <Text
              type='body-2'
              content={
                dialogConfig?.submission_step_instructions ??
                translate('ticket.create.instructions')
              }
            />
            {fields.map((field) => (
              <TicketField
                key={field.key}
                field={field}
                value={fieldValues[field.key]}
                errorMessage={fieldErrors[field.key]}
                disabled={isCreatingTicket}
                onChange={(value) => changeField(field.key, value)}
              />
            ))}
            <TextArea
              label={translate('ticket.create.description')}
              placeholder={translate('ticket.create.descriptionPlaceholder')}
              value={description}
              rows={DESCRIPTION_ROWS}
              required
              disabled={isCreatingTicket}
              onChange={setDescription}
            />
            <div className={classes.history}>
              <Text
                type='caption'
                color='text-60'
                content={translate('ticket.create.history')}
              />
              {historyMessages.length > 0 ? (
                <ChatMessages
                  messages={historyMessages}
                  compact
                  disableAutoScroll
                />
              ) : (
                <Text
                  type='body-2'
                  color='text-60'
                  content={translate('ticket.create.historyEmpty')}
                />
              )}
            </div>
            {submissionErrors.length > 0 ? (
              <div className={classes.errors} role='alert'>
                {submissionErrors.map((error) => (
                  <Text
                    key={error}
                    type='body-2'
                    color='text-danger'
                    content={error}
                  />
                ))}
              </div>
            ) : null}
          </>
        ) : null}
        {isDone ? (
          <>
            <Text
              type='body-2'
              content={
                dialogConfig?.confirmation_step_text ??
                translate('ticket.create.confirmation')
              }
            />
            <Text
              type='body-2'
              content={translate('ticket.create.email', {
                email: user?.email ?? '',
              })}
            />
            {createdTicket?.ticket_link ? (
              <Link
                type='external'
                target='blank'
                uri={createdTicket.ticket_link}
                label={translate('ticket.create.view', {
                  displayId: createdTicket.display_id,
                })}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
};
